export const normalizeBoxes = (boxes = []) => {
  if (!Array.isArray(boxes)) return [];
  return boxes.map((item) => ({
    title: item?.title || '',
    text: item?.text || '',
  }));
};

/** @deprecated use normalizeBoxes */
export const ensureFourBoxes = normalizeBoxes;

const escapeHtml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

const decodeHtml = (value) => String(value ?? '')
  .replace(/&amp;/g, '&')
  .replace(/&lt;/g, '<')
  .replace(/&gt;/g, '>')
  .replace(/&quot;/g, '"');

const stripHtml = (value) => {
  const raw = String(value ?? '');
  if (!raw.includes('<')) return raw.trim();
  if (typeof document !== 'undefined') {
    const el = document.createElement('div');
    el.innerHTML = raw;
    return (el.textContent || '').trim();
  }
  return raw.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
};

const getText = (el) => decodeHtml(el?.textContent || '').trim();

const getInnerHtml = (el) => decodeHtml(el?.innerHTML || '').trim();

const getListItems = (parent) => {
  const list = parent?.querySelector(':scope > ul, :scope ul');
  if (!list) return [];
  return Array.from(list.querySelectorAll(':scope > li')).map((li) => getText(li)).filter(Boolean);
};

const getParagraphs = (parent) => {
  return Array.from(parent?.querySelectorAll(':scope > p') || [])
    .map((p) => getText(p))
    .filter(Boolean);
};

const detectSectionType = (sectionEl, articles, listItems, paragraphs) => {
  const title = getText(sectionEl.querySelector(':scope > h2'));

  if (!articles.length && listItems.length) return 'list';
  if (!articles.length && paragraphs.length) return 'cta';

  const allArticlesHaveLists = articles.length > 0 && articles.every((a) => a.listItems?.length);
  if (allArticlesHaveLists && articles.length <= 4) return 'documents';

  const shortArticles = articles.length > 0 && articles.every((a) => !a.listItems?.length && stripHtml(a.text || '').length < 220);
  if (shortArticles && articles.length >= 2 && articles.length <= 4 && !title.toLowerCase().includes('procedure')) {
    return 'features';
  }

  return 'cards';
};

export const parseBodyHtml = (bodyHtml) => {
  if (!bodyHtml || typeof bodyHtml !== 'string') return [];

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div id="root">${bodyHtml}</div>`, 'text/html');
  const sections = Array.from(doc.querySelectorAll('#root > section'));

  return sections.map((sectionEl) => {
    const title = getText(sectionEl.querySelector(':scope > h2'));
    const articles = Array.from(sectionEl.querySelectorAll(':scope > article')).map((articleEl) => ({
      title: getText(articleEl.querySelector(':scope > h3')),
      text: getInnerHtml(articleEl.querySelector(':scope > p')) || getText(articleEl.querySelector(':scope > p')),
      listItems: getListItems(articleEl),
    })).filter((item) => item.title || item.text || item.listItems.length);

    const listItems = getListItems(sectionEl);
    const paragraphs = getParagraphs(sectionEl);

    return {
      title,
      type: detectSectionType(sectionEl, articles, listItems, paragraphs),
      items: articles,
      listItems,
      paragraphs,
    };
  }).filter((section) => section.title || section.items.length || section.listItems.length || section.paragraphs.length);
};

export const sectionsToBodyHtml = (sections = []) => {
  if (!Array.isArray(sections) || !sections.length) return '';

  return sections.map((section) => {
    const parts = ['<section>'];
    if (section.title) parts.push(`  <h2>${escapeHtml(section.title)}</h2>`);

    if (section.type === 'list' && section.listItems?.length) {
      parts.push('  <ul>');
      section.listItems.forEach((item) => parts.push(`    <li>${escapeHtml(item)}</li>`));
      parts.push('  </ul>');
    } else if (section.type === 'cta' && section.paragraphs?.length) {
      section.paragraphs.forEach((paragraph) => parts.push(`  <p>${escapeHtml(paragraph)}</p>`));
    } else if (section.items?.length) {
      section.items.forEach((item) => {
        parts.push('  <article>');
        if (item.title) parts.push(`    <h3>${escapeHtml(item.title)}</h3>`);
        if (item.text) {
          const plain = stripHtml(item.text);
          parts.push(`    <p>${escapeHtml(plain)}</p>`);
        }
        if (item.listItems?.length) {
          parts.push('    <ul>');
          item.listItems.forEach((li) => parts.push(`      <li>${escapeHtml(li)}</li>`));
          parts.push('    </ul>');
        }
        parts.push('  </article>');
      });
    }

    parts.push('</section>');
    return parts.join('\n');
  }).join('\n');
};

export const getAllSectionsFromContent = (content) => {
  if (!content) return [];
  if (Array.isArray(content.sections) && content.sections.length) {
    return normalizeSections(content.sections);
  }
  if (content.bodyHtml) {
    return normalizeSections(parseBodyHtml(content.bodyHtml));
  }
  return [];
};

export const getBoxesSectionTitle = (content) => {
  if (content?.boxesSectionTitle) return content.boxesSectionTitle;
  const sections = getAllSectionsFromContent(content);
  const first = sections.find((section) => section.items?.length);
  return first?.title || '';
};

export const getBoxesFromContent = (content) => {
  if (Array.isArray(content?.boxes) && content.boxes.some((box) => box?.title || box?.text)) {
    return normalizeBoxes(content.boxes);
  }

  const sections = getAllSectionsFromContent(content);
  const first = sections.find((section) => section.items?.length);
  if (!first) return [];

  return normalizeBoxes(first.items.map((item) => ({
    title: item.title || '',
    text: item.text || '',
  })));
};

export const getAdditionalSectionsFromContent = (content) => {
  if (Array.isArray(content?.sections) && content.sections.length) {
    return normalizeSections(content.sections);
  }

  const sections = getAllSectionsFromContent(content);
  if (!sections.length) return [];

  const hasExplicitBoxes = Array.isArray(content?.boxes) && content.boxes.some((box) => box?.title || box?.text);
  if (hasExplicitBoxes) return sections;

  return sections.slice(1);
};

export const getSectionsFromContent = (content) => getAdditionalSectionsFromContent(content);

export const normalizeSections = (sections = []) => sections.map((section) => ({
  title: section.title || '',
  type: section.type || 'cards',
  items: Array.isArray(section.items)
    ? section.items.map((item) => ({
      title: item.title || '',
      text: item.text || '',
      listItems: Array.isArray(item.listItems) ? item.listItems : [],
    }))
    : [],
  listItems: Array.isArray(section.listItems) ? section.listItems : [],
  paragraphs: Array.isArray(section.paragraphs) ? section.paragraphs : [],
}));

export const buildContentPayload = (sections) => {
  const normalized = normalizeSections(sections);
  const bodyHtml = sectionsToBodyHtml(normalized);
  return {
    sections: normalized,
    bodyHtml,
  };
};

export const buildFullContentPayload = ({ boxesSectionTitle = '', boxes = [], sections = [] } = {}) => {
  const normalizedBoxes = normalizeBoxes(boxes);
  const normalizedSections = normalizeSections(sections);
  const boxesSection = {
    title: boxesSectionTitle,
    type: 'cards',
    items: normalizedBoxes.map((box) => ({
      title: box.title,
      text: box.text,
      listItems: [],
    })),
  };

  const allSections = [boxesSection, ...normalizedSections].filter((section) => (
    section.title || section.items?.some((item) => item.title || item.text) || section.listItems?.length || section.paragraphs?.length
  ));

  return {
    boxesSectionTitle,
    boxes: normalizedBoxes,
    sections: normalizedSections,
    bodyHtml: sectionsToBodyHtml(allSections),
  };
};
